import { SimulationAmountInput } from '@/_components/inputs/simulation-amount-input'
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/_components/ui/form'
import { SegmentedControl } from '@/_components/ui/segmented-control'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/_components/ui/tooltip'
import { formatCurrency } from '@/_helpers/currency'
import { formatDecimalValue } from '@/_helpers/formatDecimalValue'
import {
  BulkSimulateApplicationInputDTOSchema,
  SIMULATION_AMOUNT_LIMITS,
  type TBulkSimulateApplicationInputDTO,
  type TSimulationMode,
} from '@doutorsim/shared/applications'
import { reaisToCents } from '@doutorsim/shared/helpers/currency'
import { zodResolver } from '@hookform/resolvers/zod'
import dayjs from 'dayjs'
import { Info } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import type { DateInterval } from 'react-day-picker'
import { useForm } from 'react-hook-form'
import { LoanDatePicker } from '../../_components/loan-date-picker'
import { SIMULATION_MODE_OPTIONS } from '../_constants'

interface SimulationFormProps {
  onSimulate: (data: TBulkSimulateApplicationInputDTO, mode: TSimulationMode) => void
}

export const SimulationForm = ({ onSimulate }: SimulationFormProps) => {
  const [formattedCurrencyValue, setFormattedCurrencyValue] = useState('')
  const [simulationMode, setSimulationMode] = useState<TSimulationMode>('product_cost')
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const form = useForm<TBulkSimulateApplicationInputDTO>({
    resolver: zodResolver(BulkSimulateApplicationInputDTOSchema),
    defaultValues: {
      disbursementDate: dayjs().add(3, 'day').format('YYYY-MM-DD'),
    },
  })

  const requestedAmount = form.watch('requestedAmount')
  const firstPaymentDate = form.watch('firstPaymentDate')

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current)

    debounceRef.current = setTimeout(() => {
      if (!requestedAmount || !firstPaymentDate) return

      form.handleSubmit((data) => {
        onSimulate({ ...data }, simulationMode)
      })()
    }, 600)

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current)
    }
  }, [requestedAmount, firstPaymentDate, simulationMode])

  const firstPaymentDateRange: DateInterval = {
    // According to Celcoin's docs, the first payment date must be equal or greater than the disbursement date,
    // and our disbursement date is typically D+3.
    // https://developers.celcoin.com.br/docs/simula%C3%A7%C3%A3o-da-ccb-1#:~:text=%7B%0A%20%20%20%20%22message%22%3A%20%22Constraint%20violation%20error%3A%20first_payment_date%3A%20The%20first_payment_date%20must%20be%20after%20the%20disbursement_date.%2C%20firstPaymentDate%3A%20Deve%20ser%20uma%20data%20no%20presente%20ou%20no%20futuro%2C%20%3A%20The%20first_payment_date%20must%20be%20equal%20or%20after%20the%20disbursement_date.%22%0A%7D
    before: dayjs().add(3, 'day').toDate(),
    after: dayjs().add(45, 'day').toDate(),
  }

  return (
    <Form {...form}>
      <form className="flex flex-col gap-6">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-brand-grayscale-7">
            Dados da simulação
          </p>
          <h2 className="mt-2 text-lg font-semibold text-brand-grayscale-12">
            Preencha os dados e veja o resultado ao lado.
          </h2>
        </div>

        <div>
          <div className="mb-2 flex items-center gap-1.5">
            <p className="text-sm font-medium">Tipo de simulação</p>
            <TooltipProvider delayDuration={200}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button type="button" aria-label="O que é o tipo de simulação?">
                    <Info size={15} />
                  </button>
                </TooltipTrigger>
                <TooltipContent className="max-w-[280px]">
                  <p>
                    <strong>Custo do Procedimento:</strong> informe o valor do procedimento; juros e
                    taxas são calculados e somados sobre ele.
                    <br />
                    <br />
                    <strong>Custo Total:</strong> informe o valor total que o paciente irá pagar,
                    com juros e taxas já incluídos.
                  </p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
          <SegmentedControl
            fullWidth
            value={simulationMode}
            onChange={setSimulationMode}
            options={SIMULATION_MODE_OPTIONS}
          />
        </div>

        <FormField
          control={form.control}
          name="requestedAmount"
          render={({ field: { onChange, value, ...rest } }) => (
            <FormItem className="w-full">
              <div className="flex items-center gap-1.5">
                <FormLabel className="required-field">
                  {simulationMode === 'total_cost'
                    ? 'Qual o orçamento do paciente?'
                    : 'Precisa de quanto?'}
                </FormLabel>
                <TooltipProvider delayDuration={200}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <button type="button" aria-label="Quais são os limites de valor?">
                        <Info size={15} />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>
                        O valor mínimo é {formatCurrency(SIMULATION_AMOUNT_LIMITS.MIN)} e o máximo é{' '}
                        {formatCurrency(SIMULATION_AMOUNT_LIMITS.MAX)}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              <FormControl>
                <SimulationAmountInput
                  value={formattedCurrencyValue}
                  onValueChange={({ floatValue, value }) => {
                    const amountInCents = floatValue ? reaisToCents(floatValue) : 0
                    onChange(amountInCents)
                    setFormattedCurrencyValue(formatDecimalValue(value))
                  }}
                  placeholder={
                    simulationMode === 'total_cost'
                      ? 'Digite o orçamento do paciente'
                      : 'Digite o valor do procedimento'
                  }
                  {...rest}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="firstPaymentDate"
          render={({ field }) => (
            <FormItem className="w-full">
              <FormLabel className="required-field">
                Melhor dia para o vencimento da parcela
              </FormLabel>
              <LoanDatePicker
                className="w-full"
                field={field}
                calendar={{
                  ...firstPaymentDateRange,
                  modifiers: {
                    disabled: [{ ...firstPaymentDateRange }],
                  },
                }}
              />
              <FormMessage />
            </FormItem>
          )}
        />
      </form>
    </Form>
  )
}
